// src/pages/CalendarView.jsx
import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

function getDaysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function getFirstDayOfMonth(year, month) { return new Date(year, month, 1).getDay(); }

export default function CalendarView({ trades, settings , lang = 'en'}) {
  const today   = new Date();
  const [year,   setYear]   = useState(today.getFullYear());
  const [month,  setMonth]  = useState(today.getMonth());
  const [display, setDisplay] = useState('$'); // '$' or '%'
  const [selected, setSelected] = useState(null);

  const startBal = settings?.startingBalance || 0;

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  // Group closed trades by date only
  const byDate = {};
  for (const t of trades.filter(t => t.status !== 'open')) {
    if (!t.date) continue;
    if (!byDate[t.date]) byDate[t.date] = [];
    byDate[t.date].push(t);
  }

  const days     = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const monthName = new Date(year, month).toLocaleString('default', { month: 'long' });
  const pad = n => String(n).padStart(2, '0');
  const dateKey = d => `${year}-${pad(month + 1)}-${pad(d)}`;

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);

  // Month summary
  const monthEntries = Object.entries(byDate).filter(([d]) => d.startsWith(`${year}-${pad(month + 1)}`));
  const monthPnl     = monthEntries.reduce((s, [, ts]) => s + ts.reduce((a, t) => a + Number(t.pnl || 0), 0), 0);
  const monthPnlPct  = startBal > 0 ? ((monthPnl / startBal) * 100).toFixed(2) : '0.00';
  const monthTrades  = monthEntries.reduce((s, [, ts]) => s + ts.length, 0);
  const monthWins    = monthEntries.reduce((s, [, ts]) => s + ts.filter(t => t.result === 'Win').length, 0);
  const winDays      = monthEntries.filter(([, ts]) => ts.some(t => t.result === 'Win')).length;

  const selectedTrades = selected ? (byDate[dateKey(selected)] || []) : [];
  const formatVal = (pnl) => {
    if (display === '%') {
      if (!startBal) return '—';
      const pct = ((pnl / startBal) * 100).toFixed(1);
      return `${pnl >= 0 ? '+' : ''}${pct}%`;
    }
    return `${pnl >= 0 ? '+' : ''}$${Math.abs(pnl).toFixed(0)}`;
  };

  return (
    <div className="fade-in" style={{ padding:'28px 32px', maxWidth:1100, margin:'0 auto' }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:600, color:'#f0f0f5', margin:0, fontFamily:"'IBM Plex Sans',sans-serif" }}>Calendar</h1>
          <p style={{ color:'#55556a', fontSize:12, margin:'3px 0 0', fontFamily:"'DM Mono',monospace" }}>Monthly trading overview</p>
        </div>
        {/* $ / % toggle */}
        <div style={{ display:'flex', background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:8, padding:3, gap:3 }}>
          {['$','%'].map(mode => (
            <button key={mode} onClick={() => setDisplay(mode)} style={{
              padding:'6px 16px', borderRadius:6, border:'none', fontSize:13, fontWeight:600,
              cursor:'pointer', fontFamily:"'DM Mono',monospace",
              background: display===mode ? '#16161f' : 'transparent',
              color: display===mode ? '#00e5a0' : '#55556a',
              transition:'all 0.15s',
              boxShadow: display===mode ? '0 1px 4px rgba(0,0,0,0.3)' : 'none',
            }}>{mode}</button>
          ))}
        </div>
      </div>

      {/* Month nav + stats */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={prevMonth} style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:8, padding:'8px 10px', cursor:'pointer', color:'#f0f0f5', display:'flex' }}><ChevronLeft size={16} /></button>
          <div style={{ fontSize:20, fontWeight:600, color:'#f0f0f5', fontFamily:"'DM Mono',monospace", minWidth:160, textAlign:'center' }}>
            {monthName} {year}
          </div>
          <button onClick={nextMonth} style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:8, padding:'8px 10px', cursor:'pointer', color:'#f0f0f5', display:'flex' }}><ChevronRight size={16} /></button>
        </div>

        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {[
            { label:'Trades',    value: monthTrades },
            { label:'Win Days',  value: winDays },
            { label:'Win Rate',  value: monthTrades > 0 ? `${Math.round((monthWins/monthTrades)*100)}%` : '—', color:'#00e5a0' },
            { label:'Month P&L', value: display === '%' ? `${Number(monthPnlPct) >= 0 ? '+' : ''}${monthPnlPct}%` : `${monthPnl >= 0 ? '+' : ''}$${monthPnl.toFixed(2)}`, color: monthPnl >= 0 ? '#00e5a0' : '#ff4d6d' },
          ].map(s => (
            <div key={s.label} style={{ textAlign:'center', background:'#16161f', border:'1px solid #1e1e2e', borderRadius:10, padding:'10px 16px' }}>
              <div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:4 }}>{s.label}</div>
              <div style={{ fontSize:16, fontWeight:600, color:s.color||'#f0f0f5', fontFamily:"'DM Mono',monospace" }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Calendar grid */}
      <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, overflow:'hidden', marginBottom:20 }}>
        {/* Day headers */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', borderBottom:'1px solid #1e1e2e' }}>
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
            <div key={d} style={{ padding:'10px 0', textAlign:'center', fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.07em' }}>{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)' }}>
          {cells.map((day, i) => {
            if (!day) return <div key={`e-${i}`} style={{ minHeight:80, borderRight:'1px solid #1a1a26', borderBottom:'1px solid #1a1a26' }} />;
            const key        = dateKey(day);
            const dayTrades  = byDate[key] || [];
            const dayPnl     = dayTrades.reduce((s, t) => s + Number(t.pnl || 0), 0);
            const isToday    = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
            const isSelected = selected === day;
            const hasTrades  = dayTrades.length > 0;
            const wins       = dayTrades.filter(t => t.result === 'Win').length;
            const losses     = dayTrades.filter(t => t.result === 'Loss').length;
            const pnlColor   = dayPnl > 0 ? '#00e5a0' : dayPnl < 0 ? '#ff4d6d' : '#ffd166';

            return (
              <div key={day}
                onClick={() => hasTrades && setSelected(isSelected ? null : day)}
                style={{
                  minHeight:80, padding:'8px 10px',
                  borderRight:'1px solid #1a1a26', borderBottom:'1px solid #1a1a26',
                  cursor: hasTrades ? 'pointer' : 'default',
                  background: isSelected ? '#1c1c28' : hasTrades ? `rgba(${dayPnl > 0 ? '0,229,160' : dayPnl < 0 ? '255,77,109' : '255,209,102'},0.04)` : 'transparent',
                  transition:'background 0.15s', position:'relative'
                }}
                onMouseEnter={e => { if (hasTrades && !isSelected) e.currentTarget.style.background = '#1c1c28'; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = hasTrades ? `rgba(${dayPnl > 0 ? '0,229,160' : dayPnl < 0 ? '255,77,109' : '255,209,102'},0.04)` : 'transparent'; }}
              >
                {/* Day number */}
                <div style={{ fontSize:12, fontWeight: isToday ? 700 : 400, color: isToday ? '#00e5a0' : '#8888a8', fontFamily:"'DM Mono',monospace", display:'inline-flex', alignItems:'center', justifyContent:'center', width: isToday ? 22 : 'auto', height: isToday ? 22 : 'auto', background: isToday ? 'rgba(0,229,160,0.15)' : 'transparent', borderRadius: isToday ? '50%' : 0, marginBottom:4 }}>
                  {day}
                </div>

                {hasTrades && (
                  <div>
                    {/* P&L or % */}
                    <div style={{ fontSize:12, fontWeight:700, fontFamily:"'DM Mono',monospace", color:pnlColor, lineHeight:1.2 }}>
                      {formatVal(dayPnl)}
                    </div>
                    {/* Show the other format smaller below */}
                    {startBal > 0 && (
                      <div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", lineHeight:1.2 }}>
                        {display === '$'
                          ? `${dayPnl >= 0 ? '+' : ''}${((dayPnl / startBal) * 100).toFixed(1)}%`
                          : `${dayPnl >= 0 ? '+' : ''}$${Math.abs(dayPnl).toFixed(0)}`
                        }
                      </div>
                    )}
                    {/* Trade count */}
                    <div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", marginTop:2 }}>
                      {dayTrades.length}t · {wins}W {losses > 0 ? `${losses}L` : ''}
                    </div>
                    {/* Result dots */}
                    <div style={{ display:'flex', gap:3, flexWrap:'wrap', marginTop:3 }}>
                      {dayTrades.map((t, ti) => (
                        <div key={ti} style={{ width:5, height:5, borderRadius:'50%', background: t.result==='Win'?'#00e5a0':t.result==='Loss'?'#ff4d6d':'#ffd166' }} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected day */}
      {selected && selectedTrades.length > 0 && (
        <div style={{ background:'#16161f', border:'1px solid #2a2a3e', borderRadius:12, padding:20, marginBottom:16 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'#f0f0f5', marginBottom:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span>{monthName} {selected} — {selectedTrades.length} trade{selectedTrades.length > 1 ? 's' : ''}</span>
            <span style={{ fontSize:13, fontFamily:"'DM Mono',monospace", color: selectedTrades.reduce((s,t)=>s+Number(t.pnl||0),0) >= 0 ? '#00e5a0' : '#ff4d6d' }}>
              {formatVal(selectedTrades.reduce((s,t)=>s+Number(t.pnl||0),0))}
              {startBal > 0 && display === '$' && (
                <span style={{ fontSize:11, color:'#55556a', marginLeft:8 }}>
                  {`(${selectedTrades.reduce((s,t)=>s+Number(t.pnl||0),0) >= 0 ? '+' : ''}${((selectedTrades.reduce((s,t)=>s+Number(t.pnl||0),0) / startBal) * 100).toFixed(2)}%)`}
                </span>
              )}
            </span>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {selectedTrades.map((t, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:14, flexWrap:'wrap', padding:'10px 14px', background:'rgba(255,255,255,0.02)', borderRadius:8, border:'1px solid #1e1e2e' }}>
                <span style={{ fontWeight:600, color:'#f0f0f5', fontSize:13, minWidth:80 }}>{t.pair}</span>
                <span style={{ color: t.direction==='Buy'?'#00e5a0':'#ff4d6d', fontSize:12, fontWeight:600 }}>{t.direction}</span>
                <span style={{ color: Number(t.pnl)>=0?'#00e5a0':'#ff4d6d', fontFamily:"'DM Mono',monospace", fontSize:13, fontWeight:600 }}>
                  {Number(t.pnl)>=0?'+':''}${Number(t.pnl).toFixed(2)}
                  {startBal > 0 && <span style={{ fontSize:10, color:'#55556a', marginLeft:6 }}>({((Number(t.pnl)/startBal)*100).toFixed(2)}%)</span>}
                </span>
                {t.rrAchieved && <span style={{ fontSize:11, color:'#8888a8', fontFamily:"'DM Mono',monospace" }}>{t.rrAchieved}R</span>}
                <span style={{ fontSize:11, color:'#55556a' }}>{t.session}</span>
                {!t.followedRules && <span style={{ fontSize:11, color:'#ff4d6d', background:'rgba(255,77,109,0.1)', padding:'2px 7px', borderRadius:5 }}>Rules broken</span>}
                {t.screenshotLink && <a href={t.screenshotLink} target="_blank" rel="noreferrer" style={{ fontSize:11, color:'#4cc9f0', textDecoration:'none', marginLeft:'auto' }}>Chart ↗</a>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div style={{ display:'flex', gap:16, marginTop:8, flexWrap:'wrap', alignItems:'center' }}>
        {[['#00e5a0','Win'],['#ff4d6d','Loss'],['#ffd166','Breakeven']].map(([c,l]) => (
          <div key={l} style={{ display:'flex', alignItems:'center', gap:6 }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:c }} />
            <span style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace" }}>{l}</span>
          </div>
        ))}
        <div style={{ marginLeft:'auto', fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace" }}>
          Click any trading day to see details
        </div>
      </div>
    </div>
  );
}
